import type { UserIncomeReportRowType } from '@econtractor/types';
import type { TFunction } from 'i18next';
import type { ReportPeriodType } from '../../ReportDate/periodUtils';
import type { ReportUsersObjectFilter, ReportUsersViewMode } from '../../helper/reportUsersStorage';
import {
    exportDate,
    exportReportWorkbook,
    formatExportPeriod,
    isExportLocalMode,
    moneyColumns,
    roundExportMoney,
    type ExportCell,
    type ExportColumn,
} from '../../helper/exportReportWorkbook';

export interface ExportUsersReportParams {
    users: UserIncomeReportRowType[];
    dates: [number, number] | null;
    period: ReportPeriodType;
    viewMode: ReportUsersViewMode;
    objectFilter: ReportUsersObjectFilter;
    rate: number;
    t: TFunction;
}

const headerLines = (
    t: TFunction,
    period: ReportPeriodType,
    dates: [number, number] | null,
    viewMode: ReportUsersViewMode,
    objectFilter: ReportUsersObjectFilter,
) => [
    { label: t('export.period'), value: formatExportPeriod(t(`dateFilter.${period}`), dates) },
    {
        label: t('export.view'),
        value: viewMode === 'detail' ? t('usersView.detailHint') : t('usersView.summaryHint'),
    },
    { label: t('usersFields.object'), value: t(`usersFilter.${objectFilter}`) },
];

export const exportUsersReport = async ({
    users,
    dates,
    period,
    viewMode,
    objectFilter,
    rate,
    t,
}: ExportUsersReportParams): Promise<boolean> => {
    if (!users.length) return false;

    const localMode = isExportLocalMode(rate);
    const title = t('common:SideBarUl.itemRepostsUsers');
    const incomeColumns = moneyColumns('income', 'incomeLocal', t('usersFields.income'), rate);

    if (viewMode === 'summary') {
        const rows = users.map((user) => {
            const row: Record<string, ExportCell> = {
                user: user.userName || '',
                income: roundExportMoney(user.amount),
            };
            if (!localMode) row.incomeLocal = roundExportMoney(user.amountRate);
            return row;
        });

        await exportReportWorkbook({
            filePrefix: 'report-users',
            sheetName: title,
            title,
            headerLines: headerLines(t, period, dates, viewMode, objectFilter),
            columns: [
                { key: 'user', header: t('usersFields.user'), width: 32 },
                ...incomeColumns,
            ],
            rows,
            totalLabel: t('export.total'),
        });
        return true;
    }

    const columns: ExportColumn[] = [
        { key: 'user', header: t('usersFields.user'), width: 28 },
        { key: 'object', header: t('usersFields.object'), width: 32 },
        { key: 'status', header: t('export.objectStatus'), width: 22 },
        { key: 'service', header: t('usersFields.service'), width: 28 },
        { key: 'date', header: t('usersFields.date'), width: 16, kind: 'date' },
        ...incomeColumns,
    ];

    const rows: Record<string, ExportCell>[] = [];
    for (const user of users) {
        for (const item of user.rows || []) {
            const status = Number(item.objectStatus);
            const row: Record<string, ExportCell> = {
                user: user.userName || '',
                object: item.objectName || '',
                status: t(`Objects:status.${status}`, { defaultValue: String(status) }),
                service: item.serviceName || '',
                date: exportDate(item.dt),
                income: roundExportMoney(item.amount),
            };
            if (!localMode) row.incomeLocal = roundExportMoney(item.amountRate);
            rows.push(row);
        }
    }

    if (!rows.length) return false;

    await exportReportWorkbook({
        filePrefix: 'report-users',
        sheetName: title,
        title,
        headerLines: headerLines(t, period, dates, viewMode, objectFilter),
        columns,
        rows,
        totalLabel: t('export.total'),
    });

    return true;
};
